import React from 'react';
import { useColorMode } from '../../contexts/ColorModeContext';

const LIGHT_COLOR = '#3C3A3A';
const DARK_COLOR = '#D9D9D9';
const GOLD_COLOR = '#BAA87B';

interface VellumIconProps {
  /** Override the resolved dark-element color. Gold accent is always #BAA87B. */
  color?: string;
  /** Height of the SVG; width scales proportionally (311:336 aspect ratio). */
  height?: number;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Renders the Vellum feather-V icon. The dark strokes automatically adapt to
 * the active colour mode: #3C3A3A in light mode, #D9D9D9 in dark mode. The
 * gold accent (#BAA87B) is always preserved regardless of colour mode.
 */
export function VellumIcon({ color, height = 32, className, style }: VellumIconProps) {
  const { resolvedMode } = useColorMode();
  const fill = color ?? (resolvedMode === 'dark' ? DARK_COLOR : LIGHT_COLOR);

  // Aspect ratio: 311.14 × 335.8 ≈ 1:1.08
  const width = (height * 311.14) / 335.8;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 312 336"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      aria-label="Vellum icon"
      role="img"
    >
      <g clipPath="url(#vellum-icon-clip)">
        <path
          d="M122.95 273.73C126.52 258.61 131.27 244.14 137.2 230.26L91.11 118.29C90.21 115.6 89.77 112.91 89.77 110.22C89.77 107.53 90.95 104.67 93.3 101.65C95.65 98.63 99.29 97.11 104.23 97.11H106.92V93.75H0V97.11H2.69C8.07 97.11 13.33 98.74 18.49 101.98C23.64 105.23 27.9 110 31.27 116.27L117.88 302.93C119.06 292.38 120.99 282.06 122.95 273.73Z"
          fill={fill}
        />
        <path
          d="M303.15 0.0599968C303.62 0.419997 306.68 9.91 307.08 11.29C311.84 27.38 312.75 46.18 307.99 62.36C295.08 106.32 253.61 115.4 220.83 139.53C181.01 168.83 154.2 219.16 147.43 267.9C146.44 275.03 145.61 282.9 146.79 290.25C146.96 291.34 147.69 295.82 148.74 295.81C149.17 295.81 154.91 291.12 155.49 290.56C167.33 279 176.89 258.68 187.87 245.45C189.37 243.64 190.81 241.31 191.92 244.97C173.44 275.45 155.28 306.22 134.88 335.46L133.23 335.81C131.91 331.43 129.88 327.25 128.88 322.76C125.1 305.86 128.93 279.72 132.93 262.76C140.99 228.59 155.71 198.07 177.39 170.59C197.92 144.55 224.14 126.42 247.02 102.83C274.47 74.54 296.62 40.67 302.11 0.709997C302.18 0.259997 302.86 -0.150003 303.15 0.0699968V0.0599968Z"
          fill={fill}
        />
        <path
          d="M287.81 31.88C288.47 32.65 285.29 39.61 284.7 40.93C268.4 77.52 235.61 104.07 206.5 129.95C180.34 153.21 159.53 176.94 145.13 209.43C144.27 211.36 142.48 217.6 141.62 218.65C141.28 219.06 140.52 219.23 140.33 219.04C139.98 218.69 140.26 210.41 140.31 209.11C141.54 177.62 154.56 150.91 175.71 128.47C206.83 95.46 247.86 75.18 278.8 41.69C281.35 38.93 283.82 35.36 286.3 32.81C286.73 32.37 287.1 31.77 287.8 31.89L287.81 31.88Z"
          fill={fill}
        />
        {/* Gold accent paths — colour is fixed regardless of theme */}
        <path
          d="M309.14 79.05C309.36 79.32 309.2 85.05 309.14 85.97C307.48 110.29 293.9 133.51 272.41 145.07C247.75 158.34 221.49 159.15 196.79 175.39C194.63 176.81 191.21 179.94 189.19 180.9C188.6 181.18 188.16 181.36 187.48 181.24C187.13 180.93 191.12 175.33 191.6 174.7C205.55 156.54 223.4 143.15 243.14 131.91C266.8 118.44 294.47 108.64 307.15 82.5C307.59 81.59 307.86 80.33 308.29 79.53C308.46 79.22 308.63 78.44 309.12 79.05H309.14Z"
          fill={GOLD_COLOR}
        />
        <path
          d="M273.95 151.67C267.15 165.6 252.5 174.67 238.1 179.26C222.45 184.24 206.1 182.12 191.27 189.71L178.5 196.96C178.45 196.31 178.45 195.73 178.7 195.11C179.67 192.75 187.9 185.78 190.3 183.88C200.83 175.54 213.35 169.39 226.4 166.08C239.32 162.8 252.95 162.26 265.22 156.62L273.95 151.69V151.67Z"
          fill={GOLD_COLOR}
        />
        <path
          d="M222.67 188.35C222.89 189.21 222.51 188.97 222.2 189.21C218.79 191.87 209.42 193.67 205.01 195.21C190.56 200.27 179.23 206.87 169.74 219.08C167.8 221.57 166.46 224.48 164.63 226.99C164.49 227.18 163.48 228.41 163.16 228.03C166.21 219.5 169.19 210.56 175.32 203.7C184.55 193.37 201.95 186.65 215.75 187.62C217.97 187.78 220.27 188.83 222.68 188.36L222.67 188.35Z"
          fill={GOLD_COLOR}
        />
      </g>
      <defs>
        <clipPath id="vellum-icon-clip">
          <rect width="311.14" height="335.8" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}
